import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { History, FileText, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const prettyPeriod = (year, month, period) => {
  const m = MONTHS[(month || 1) - 1];
  if (period === "first_half") return `${m} ${year} · 1st half`;
  if (period === "second_half") return `${m} ${year} · 2nd half`;
  return `${m} ${year} · Monthly`;
};

export default function PayslipHistoryDialog({ open, onOpenChange, branchId, employeeId, employeeName, currency }) {
  const peso = (n) => `${currency || "₱"}${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!open || !branchId || !employeeId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setSelected(null);
      try {
        const { data } = await api.get("/payslip/history", { params: { branch_id: branchId, employee_id: employeeId } });
        if (!cancelled) setItems(data || []);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [open, branchId, employeeId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl border-brand-purple/20 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="payslip-history-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-2">
            <History className="w-5 h-5 text-brand-purple" /> Pay-Slip History
          </DialogTitle>
          <DialogDescription>
            Saved snapshots for <span className="font-semibold text-foreground">{employeeName}</span>. Each snapshot is immutable — it preserves the pay-slip exactly as it was at save time, regardless of future data changes.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center text-brand-inkMuted">Loading...</div>
        ) : selected ? (
          <SnapshotView snapshot={selected} peso={peso} onBack={() => setSelected(null)} />
        ) : items.length === 0 ? (
          <div className="py-10 text-center text-brand-inkMuted text-sm" data-testid="payslip-history-empty">
            No saved snapshots yet. Click <span className="font-semibold text-foreground">Save &amp; Mark as Paid</span> to create one.
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((it) => (
              <button
                key={it.id}
                onClick={() => setSelected(it)}
                className="w-full text-left rounded-2xl border border-brand-purple/15 hover:border-brand-purple hover:bg-brand-purpleLight/30 px-4 py-3 transition-colors"
                data-testid={`payslip-history-row-${it.id}`}
              >
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2 text-sm">
                    <FileText className="w-4 h-4 text-brand-purple" />
                    <span className="font-heading font-semibold text-foreground">
                      {prettyPeriod(it.year, it.month, it.period)}
                    </span>
                  </div>
                  <span className="font-heading font-semibold text-brand-purple">
                    {peso(it?.disbursement?.amount)}
                  </span>
                </div>
                <p className="text-[11px] text-brand-inkMuted mt-0.5">
                  Saved {new Date(it.saved_at).toLocaleString()} by {it.saved_by_name || "—"} · {Array.isArray(it?.disbursement?.splits) && it.disbursement.splits.length >= 2 ? `Split (${it.disbursement.splits.length})` : it?.disbursement?.mode}
                  {(!Array.isArray(it?.disbursement?.splits) || it.disbursement.splits.length < 2) && it?.disbursement?.reference_no && ` · Ref ${it.disbursement.reference_no}`}
                </p>
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SnapshotView({ snapshot, peso, onBack }) {
  const s = snapshot.snapshot || {};
  const d = snapshot.disbursement || {};
  return (
    <div className="space-y-3">
      <Button type="button" variant="outline" size="sm" onClick={onBack} className="rounded-full border-brand-purple/30">
        <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Back to list
      </Button>
      <div className="rounded-2xl bg-[#F1F5FF] border border-brand-purple/15 p-4">
        <p className="font-heading text-lg font-semibold">{prettyPeriod(snapshot.year, snapshot.month, snapshot.period)}</p>
        <p className="text-xs text-brand-inkMuted">Saved {new Date(snapshot.saved_at).toLocaleString()} by {snapshot.saved_by_name || "—"}</p>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <Stat label="Gross Pay" value={peso(s.gross_pay)} />
        <Stat label="Net Pay" value={peso(s.net_pay ?? d.amount)} highlight />
        <Stat label="Disbursed" value={peso(d.amount)} />
      </div>

      <div className="rounded-2xl border border-brand-purple/15 p-3">
        <p className="font-heading font-semibold text-sm mb-2">Disbursement</p>
        <Row label="Date" value={d.date || "—"} />
        {d.expense_branch_id && d.expense_branch_id !== snapshot.branch_id && (
          <Row label="Charged to branch" value={d.expense_branch_name || d.expense_branch_id} />
        )}
        {Array.isArray(d.splits) && d.splits.length >= 2 ? (
          <div className="mt-1">
            <p className="text-[10px] uppercase tracking-wider text-brand-inkMuted font-heading mb-1">Payment splits</p>
            {d.splits.map((sp, i) => (
              <div key={i} className="flex items-center justify-between py-1 text-sm border-b border-brand-purple/5 last:border-0" data-testid={`payslip-history-split-${i}`}>
                <span className="text-brand-inkMuted">{(sp.mode || "cash")}{sp.reference_no ? ` · Ref ${sp.reference_no}` : ""}</span>
                <span className="text-foreground font-heading tabular-nums">{peso(sp.amount)}</span>
              </div>
            ))}
          </div>
        ) : (
          <>
            <Row label="Mode" value={d.mode || "—"} />
            <Row label="Reference No." value={d.reference_no || "—"} mono />
          </>
        )}
        <Row label="Disbursed By" value={d.disbursed_by || "—"} />
        {d.note && <Row label="Notes" value={d.note} />}
      </div>

      {Array.isArray(s.deductions) && s.deductions.length > 0 && (
        <div className="rounded-2xl border border-brand-purple/15 p-3">
          <p className="font-heading font-semibold text-sm mb-2">Deductions snapshot</p>
          {s.deductions.map((dd, i) => (
            <Row key={i} label={dd.label || `Item ${i + 1}`} value={peso(dd.amount)} />
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className={`rounded-2xl border p-3 ${highlight ? "border-brand-purple bg-brand-purpleLight/40" : "border-brand-purple/15"}`}>
      <p className="text-[10px] uppercase tracking-wider text-brand-inkMuted font-heading">{label}</p>
      <p className={`text-lg font-heading font-bold ${highlight ? "text-brand-purpleHover" : "text-foreground"}`}>{value}</p>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex items-center justify-between py-1 text-sm border-b border-brand-purple/5 last:border-0">
      <span className="text-brand-inkMuted">{label}</span>
      <span className={`text-foreground ${mono ? "font-mono text-xs" : ""}`}>{value}</span>
    </div>
  );
}
